import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * #229: how a size change reaches the cart. Switch to `true` only if the preview shows the
 * one-request form misbehaving.
 *
 * false (the default): ONE request to /cart/update, `updates: { <old line key>: 0,
 * <new size>: <quantity> }`. Shopify's Ajax Cart API reference says a key in `updates` may be
 * a line item key or a variant id, but it does not say one request may MIX the two, and this
 * does whenever the new size is not in the cart yet (its key is then the variant id, so
 * Shopify adds a line). When the new size is already in the cart, both keys are line keys,
 * which is the documented form.
 *
 * true: TWO requests. /cart/add.js adds the new size FIRST and only then /cart/change removes
 * the old line, so a failure part-way leaves the customer with both sizes rather than
 * neither. /cart/add.js ADDS to a line already in the cart ("quantity is the amount of the
 * variant that you want to add"), so it is sent the moved quantity, not the new total.
 */
const SIZE_SWAP_TWO_STEP = false;

const SIZE_SWAP_FAILED = 'The size could not be changed. Please try again.';

// #229: after a request whose outcome is unknown (it never answered). A plain "try again"
// could double the units if Shopify had in fact applied it, so this asks for a look first.
const SIZE_SWAP_UNKNOWN = 'Something went wrong changing the size. Please check your bag before trying again.';

/**
 * #229: true when a line (Ajax cart JSON) carries a property the customer can see, i.e. one
 * whose name does not start with "_". The same test the Liquid applies.
 * @param {{properties?: Record<string, unknown> | null}} item
 * @returns {boolean}
 */
function hasVisibleProperties(item) {
  return Object.keys(item.properties ?? {}).some((name) => !name.startsWith('_'));
}

/**
 * #229: a "plain" line (Ajax cart JSON), the same test as the Liquid's: no property the
 * customer can see, no subscription, not part of a bundle.
 * @param {{properties?: Record<string, unknown> | null, selling_plan_allocation?: unknown, parent_relationship?: unknown}} item
 * @returns {boolean}
 */
function isPlainLine(item) {
  return !hasVisibleProperties(item) && !item.selling_plan_allocation && !item.parent_relationship;
}

/**
 * #229: one size change, as read from the page.
 * @typedef {object} SizeSwap
 * @property {string} lineKey - The line being moved.
 * @property {string} productId
 * @property {string} fromVariantId
 * @property {string} from - The size it is now.
 * @property {number} fromInCart - Units of the old size in the cart before the change, on any line.
 * @property {string} toVariantId
 * @property {string} to - The size it moves to.
 * @property {number} quantity - The line's quantity, all of which moves.
 * @property {number} inCart - Units of the new size already in the cart, on any line.
 * @property {string} mergeKey - The new size's first plain line already in the cart, or ''.
 * @property {number} mergeQuantity - That line's quantity, or 0.
 * @property {boolean} separateLine - The size is in the cart only on lines that are not plain
 *   (a property the customer can see, a subscription), so it needs a line of its own.
 * @property {boolean} soldOut
 */

/**
 * #229: what a size change came back with.
 * @typedef {object} SizeSwapResult
 * @property {string} [error] - What to tell the customer when the change did not go through whole.
 * @property {'moved' | 'unchanged' | 'both' | 'lost' | 'short' | 'unknown'} [outcome] - What the returned
 *   cart shows, judged by unit counts (#checkSwap).
 * @property {Record<string, string> | null} [sections] - Section HTML to show.
 * @property {any} [cart] - The cart JSON, when the response was a whole cart.
 * @property {string | null} [newKey] - A line now holding the new size, for focus only.
 * @property {string | null} [messageKey] - The line to show the message on.
 * @property {string} [messageVariantId] - Failing that, a line of this variant.
 * @property {number} [arrived] - Units of the new size added.
 * @property {number} [missing] - Units that left the old size without reaching the new one.
 * @property {boolean} [changed] - A failure that still changed the cart, so it is re-drawn.
 * @property {boolean} [rerender] - The outcome is not known for certain: re-draw from the
 *   server rather than from any sections in hand.
 */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /**
   * The quantity change still waiting out its 300ms debounce, if any. #229 keeps it so a size
   * change can send it FIRST rather than drop it or race it: /cart/change addresses a line by
   * its number, and a swap removes a line, which renumbers the ones after it.
   * @type {QuantitySelectorUpdateEvent | null}
   */
  #pendingQuantityEvent = null;

  /**
   * The quantity request in flight, if any (#229), so a size change can wait for it.
   * @type {Promise<void> | null}
   */
  #quantityRequest = null;

  /** #229: true from a size tap until that change has settled. The double-tap guard. */
  #sizeSwapInFlight = false;

  /**
   * #229: the drawer this component sits in, if any; closing it clears a visible size notice.
   * @type {HTMLElement | null}
   */
  #sizeNoticeHost = null;

  // The same 300ms trailing debounce as before #229; it now also records what is pending.
  #quantityDebounce = debounce((/** @type {QuantitySelectorUpdateEvent} */ event) => {
    this.#pendingQuantityEvent = null;
    this.#onQuantityChange(event);
  }, 300);

  /** @param {QuantitySelectorUpdateEvent} event */
  #debouncedOnChange = (event) => {
    this.#pendingQuantityEvent = event;
    this.#quantityDebounce(event);
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('keydown', this.#onSizePickerKeydown);
    // 'dialog:close' is DialogCloseEvent.eventName (assets/dialog.js), sent on the drawer itself.
    this.#sizeNoticeHost = this.closest('cart-drawer-component');
    this.#sizeNoticeHost?.addEventListener('dialog:close', this.#clearSizeNotice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('keydown', this.#onSizePickerKeydown);
    this.#sizeNoticeHost?.removeEventListener('dialog:close', this.#clearSizeNotice);
    this.#sizeNoticeHost = null;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#rowForLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Finds the row for a cart LINE INDEX.
   *
   * NOT positional. `cartItemRows[line - 1]` only works while the rendered list matches
   * the cart's own line order, and it does not: the free-gift tote is moved to the bottom
   * for display, and Advanced Shipping Protection is filtered out of the list entirely
   * (#58). With protection in the cart, removing a row animated the WRONG row — or, for
   * the last row, resolved to undefined and animated nothing. The row now carries its
   * cart line number, so this matches by identity.
   * @param {number} line - The cart line index (1-based).
   * @returns {HTMLTableRowElement | undefined}
   */
  #rowForLine(line) {
    return [].concat(this.refs.cartItemRows ?? []).find((row) => row.dataset.lineIndex === String(line));
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#rowForLine(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const request = fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        if (this.#quantityRequest === request) this.#quantityRequest = null;
      });

    this.#quantityRequest = request;
    return request;
  }

  /**
   * #229: opens or closes a line's size buttons. Wired by `on:click="/toggleSizePicker"` on the
   * toggle in snippets/cart-products.liquid; assets/component.js sends a bare `/method` to the
   * nearest `*-component` ancestor, which for a cart row is this one. Opening one picker closes
   * any other. Focus stays on the toggle, as for any disclosure button.
   * @param {Event} event - The click; its target is the toggle.
   */
  toggleSizePicker(event) {
    const toggle = event.target;
    if (!(toggle instanceof HTMLElement)) return;

    const open = toggle.getAttribute('aria-expanded') !== 'true';

    for (const other of this.querySelectorAll('.cart-items__size-toggle[aria-expanded="true"]')) {
      if (other !== toggle && other instanceof HTMLElement) this.#setSizePicker(other, false);
    }

    this.#setSizePicker(toggle, open);
  }

  /**
   * @param {HTMLElement} toggle - A size toggle.
   * @returns {HTMLElement | null} The size buttons it controls, if they are in this component.
   */
  #sizePanelFor(toggle) {
    const id = toggle.getAttribute('aria-controls');
    const panel = id ? document.getElementById(id) : null;

    return panel && this.contains(panel) ? panel : null;
  }

  /**
   * @param {HTMLElement} toggle - A size toggle.
   * @param {boolean} open - Whether its size buttons should show.
   */
  #setSizePicker(toggle, open) {
    const panel = this.#sizePanelFor(toggle);
    if (!panel) return;

    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
  }

  /**
   * #229: Escape closes an open size picker and puts focus back on its toggle. It stops the
   * key there, so in the drawer the same Escape does not also close the drawer: the drawer's
   * own Escape listener sits on cart-drawer-component, an ancestor (assets/dialog.js), and
   * preventDefault stops the browser's own close of the modal <dialog>. With no picker open
   * the key passes through and closes the drawer as before.
   * @param {KeyboardEvent} event
   */
  #onSizePickerKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const toggle = this.querySelector('.cart-items__size-toggle[aria-expanded="true"]');
    if (!(toggle instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#setSizePicker(toggle, false);
    toggle.focus();
  };

  /**
   * #229: moves a cart line to the size tapped, keeping its quantity.
   *
   * Wired by `on:click="/swapSize"` on each size button the line can change to; the current
   * size and sold-out sizes carry no handler. In order:
   *  1. A second tap while a change is under way is ignored.
   *  2. A quantity change still waiting out its debounce is sent NOW, and any quantity request
   *     in flight is waited for, so the two never race. The line is then re-read from the page,
   *     which that request has re-rendered: if it removed the line there is nothing to move.
   *  3. One request (see SIZE_SWAP_TWO_STEP) removes the line and puts its quantity on the new
   *     size: on that size's existing plain line, by its key, if it has one; otherwise as a new
   *     line.
   *  4. The returned cart is judged by UNIT COUNTS per variant, never by line key. Shopify
   *     changes a line's key when its discounts change, and this store's automatic bundle
   *     discounts count across lines, so a swap can re-key lines mid-request. Units that left
   *     the old size without reaching the new one are put back (#checkSwap, #restoreMissing).
   *  5. The header count is read from the returned `[ref="cartItemCount"]` span, the #150
   *     contract in snippets/cart-display-count.liquid, and left out if it cannot be read. The
   *     event's source, 'cart-size-change', is deliberately not in assets/cart-drawer.js's
   *     AUTO_OPEN_SOURCES.
   *  6. Focus goes to the moved line's size toggle (by key, or failing that by variant) and
   *     "Size changed to L" is announced through the status region in snippets/cart-products.liquid.
   * After a request that failed or never answered, the cart is re-drawn from the server before
   * the message, so no row keeps a key or line number the server no longer has.
   * @param {Event} event - The click; its target is the size button.
   */
  async swapSize(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.disabled || button.getAttribute('aria-pressed') === 'true') return;
    if (this.#sizeSwapInFlight) return;

    const row = button.closest('tr[data-key]');
    const lineKey = row instanceof HTMLElement ? row.dataset.key : undefined;
    const toVariantId = button.dataset.variantId;
    if (!lineKey || !toVariantId) return;

    this.#sizeSwapInFlight = true;
    this.#clearSizeNotice();
    const marker = cartPerformance.createStartingMarker('size-change:user-action');
    /** @type {SizeSwap | null} */
    let swap = null;

    try {
      await this.#settleQuantityChanges();

      swap = this.#readSizeSwap(lineKey, toVariantId);
      if (!swap) return;

      if (swap.soldOut) {
        this.#showSwapError([lineKey], `Size ${swap.to} is sold out.`);
        return;
      }

      this.#disableCartItems();
      this.refs.cartTotal?.shimmer();

      // A size held only on lines that are not plain cannot be reached by /cart/update: by
      // variant id it would change THAT line ("the first matching line item is updated"). Its
      // add.js, with no properties, makes a separate plain line instead.
      const twoStep = SIZE_SWAP_TWO_STEP || (swap.separateLine && !swap.mergeKey);
      let result = twoStep ? await this.#swapInTwoSteps(swap) : await this.#swapInOneRequest(swap);

      // Units left the old size without reaching the new one: put them back first.
      if (result.outcome === 'lost' || result.outcome === 'short') result = await this.#restoreMissing(swap, result);

      resetShimmer(this);

      if (result.rerender) {
        // An outcome not known for certain: the cart as the server has it, not sections in hand.
        await this.#rerenderAfterFailure();
      } else if (!result.error || result.sections || result.changed) {
        // With no sections in a response, the section is fetched instead (#applySwapResult).
        await this.#applySwapResult(result);
      } else {
        // A request Shopify refused: re-draw from the server all the same.
        await this.#rerenderAfterFailure();
      }

      if (result.error) {
        const errorRow = this.#showSwapError(
          [result.messageKey, lineKey, result.newKey],
          result.error,
          [result.messageVariantId, swap.fromVariantId, swap.toVariantId]
        );
        this.#recoverFocus(errorRow);
        return;
      }

      const newRow = (result.newKey ? this.#rowForKey(result.newKey) : undefined) ?? this.#rowForVariant(swap.toVariantId);
      const focusTarget =
        newRow?.querySelector('.cart-items__size-toggle') ?? newRow?.querySelector('a[href], button, input');
      if (focusTarget instanceof HTMLElement) focusTarget.focus();

      this.#announce(`Size changed to ${swap.to}`);
      this.#trackSizeChange(swap);
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      // The request may or may not have reached Shopify: show the cart as the server has it,
      // and do not suggest a plain retry, which could double the units.
      await this.#rerenderAfterFailure();
      const errorRow = this.#showSwapError([lineKey], SIZE_SWAP_UNKNOWN, swap ? [swap.fromVariantId, swap.toVariantId] : []);
      this.#recoverFocus(errorRow);
    } finally {
      this.#enableCartItems();
      this.#sizeSwapInFlight = false;
      cartPerformance.measureFromMarker(marker);
    }
  }

  /**
   * #229: sends a quantity change still waiting out its debounce, then waits for whichever
   * quantity request is in flight. Never throws.
   */
  async #settleQuantityChanges() {
    const pending = this.#pendingQuantityEvent;

    if (pending) {
      this.#quantityDebounce.cancel();
      this.#pendingQuantityEvent = null;
      this.#onQuantityChange(pending);
    }

    if (this.#quantityRequest) {
      try {
        await this.#quantityRequest;
      } catch (error) {
        // updateQuantity reports its own errors; the line is re-read from the page either way.
      }
    }
  }

  /**
   * @param {string} key - A cart line key.
   * @returns {HTMLElement | undefined} That line's row in this component.
   */
  #rowForKey(key) {
    return Array.from(this.querySelectorAll('tr[data-key]')).find(
      (row) => row instanceof HTMLElement && row.dataset.key === key
    );
  }

  /**
   * #229: a row of this variant, for when a line's key has changed. Shopify's line keys are
   * "the ID of the variant ... and a hash ... separated by a colon", so the prefix is stable.
   * Prefers a row with a size toggle, which a plain line has.
   * @param {string | null | undefined} variantId
   * @returns {HTMLElement | undefined}
   */
  #rowForVariant(variantId) {
    if (!variantId) return undefined;

    const rows = Array.from(this.querySelectorAll('tr[data-key]')).filter(
      (row) => row instanceof HTMLElement && (row.dataset.key ?? '').startsWith(`${variantId}:`)
    );

    return /** @type {HTMLElement | undefined} */ (rows.find((row) => row.querySelector('.cart-items__size-toggle')) ?? rows[0]);
  }

  /**
   * #229: everything a size change needs, read from the page as it is NOW.
   * @param {string} lineKey - The line being moved.
   * @param {string} toVariantId - The size it moves to.
   * @returns {SizeSwap | null} Null when the line, or that size on it, is no longer there.
   */
  #readSizeSwap(lineKey, toVariantId) {
    const row = this.#rowForKey(lineKey);
    const panel = row?.querySelector('.cart-items__size-options');
    if (!row || !(panel instanceof HTMLElement)) return null;

    const option = Array.from(panel.querySelectorAll('.cart-items__size-option')).find(
      (candidate) => candidate instanceof HTMLElement && candidate.dataset.variantId === String(toVariantId)
    );
    if (!(option instanceof HTMLButtonElement)) return null;
    if (option.getAttribute('aria-pressed') === 'true') return null;

    const quantity = parseInt(panel.dataset.lineQuantity ?? '', 10);
    if (!(quantity > 0)) return null;

    // Units of the old size in the cart, on any line: the current size's own button carries it.
    const current = panel.querySelector('.cart-items__size-option[aria-pressed="true"]');
    const fromInCart = parseInt(current instanceof HTMLElement ? current.dataset.inCart ?? '' : '', 10);

    return {
      lineKey,
      productId: panel.dataset.productId ?? '',
      fromVariantId: panel.dataset.variantId ?? '',
      from: panel.dataset.size ?? '',
      fromInCart: fromInCart >= quantity ? fromInCart : quantity,
      toVariantId: String(toVariantId),
      to: option.dataset.size ?? '',
      quantity,
      inCart: parseInt(option.dataset.inCart ?? '0', 10) || 0,
      mergeKey: option.dataset.mergeKey ?? '',
      mergeQuantity: parseInt(option.dataset.mergeQuantity ?? '0', 10) || 0,
      separateLine: option.hasAttribute('data-separate-line'),
      soldOut: option.disabled,
    };
  }

  /**
   * @returns {string[]} The section id of every cart-items-component on the page, this one first.
   */
  #sectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionsToUpdate);
  }

  /**
   * #229: the one-request form (the default; see SIZE_SWAP_TWO_STEP).
   * @param {SizeSwap} swap
   * @returns {Promise<SizeSwapResult>}
   */
  async #swapInOneRequest(swap) {
    /** @type {Record<string, number>} */
    const updates = { [swap.lineKey]: 0 };

    if (swap.mergeKey) {
      updates[swap.mergeKey] = swap.mergeQuantity + swap.quantity;
    } else {
      updates[swap.toVariantId] = swap.inCart + swap.quantity;
    }

    const response = await this.#postCart(Theme.routes.cart_update_url, {
      updates,
      sections: this.#sectionsToUpdate().join(','),
      sections_url: window.location.pathname,
    });

    if (!response.ok) return this.#checkRefusal(swap, response.message, null, 'one-request refused');

    return this.#checkSwap(swap, response.data, null, 'one-request');
  }

  /**
   * #229: after a request Shopify refused (or answered unreadably). Shopify does not document
   * that a refused /cart/update or /cart/change changed nothing, so the cart is read again
   * (/cart.js) and judged by units like any other answer: a line taken off without the new size
   * arriving is put back (#restoreMissing). If nothing moved, Shopify's own message is kept.
   * With no readable cart, nothing is claimed beyond "check your bag".
   * @param {SizeSwap} swap
   * @param {string} message - What Shopify said.
   * @param {string | null} preferredKey
   * @param {string} mode
   * @returns {Promise<SizeSwapResult>}
   */
  async #checkRefusal(swap, message, preferredKey, mode) {
    const cart = await this.#readCartNow();
    if (!cart) return { error: SIZE_SWAP_UNKNOWN, outcome: 'unknown', rerender: true };

    const verdict = this.#checkSwap(swap, cart, preferredKey, mode);
    if (verdict.outcome === 'unchanged') return { error: message };

    // A /cart.js answer carries no sections. Lost and short go on to #restoreMissing, whose
    // answer does; everything else is re-drawn from the server, which also tells the header
    // the new count (#rerenderAfterFailure). On /cart the header is a separate section.
    const restoring = verdict.outcome === 'lost' || verdict.outcome === 'short';
    return { ...verdict, sections: null, changed: true, rerender: !restoring };
  }

  /**
   * #229: the cart as Shopify has it now (/cart.js), or null if it cannot be read.
   * @returns {Promise<any | null>}
   */
  async #readCartNow() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;

      const cart = await response.json();
      return Array.isArray(cart?.items) ? cart : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * #229: the two-request form (see SIZE_SWAP_TWO_STEP): add the new size, then take the moved
   * units off the old line. The add can cross a bundle discount's threshold, and a line's key
   * carries its discounts, so the old line is found again in a fresh read of the cart rather
   * than by the key it had before (#findOldLineNow). Both requests ask for the sections, so if
   * the second fails the cart can still be shown as it really is, holding both sizes.
   * @param {SizeSwap} swap
   * @returns {Promise<SizeSwapResult>}
   */
  async #swapInTwoSteps(swap) {
    const sections = this.#sectionsToUpdate().join(',');

    const added = await this.#postCart(Theme.routes.cart_add_url, {
      items: [{ id: Number(swap.toVariantId), quantity: swap.quantity }],
      sections,
      sections_url: window.location.pathname,
    });

    if (!added.ok) return { error: added.message };

    const addedKey = added.data.items?.[0]?.key ?? null;
    const oldLine = await this.#findOldLineNow(swap);
    const removed = oldLine
      ? await this.#postCart(Theme.routes.cart_change_url, {
          id: oldLine.key,
          quantity: Math.max(0, oldLine.quantity - swap.quantity),
          sections,
          sections_url: window.location.pathname,
        })
      : null;

    // The old line could not be told apart: nothing was taken off, so both sizes are in the
    // bag, as the add's own sections show.
    if (!removed) {
      return {
        outcome: 'both',
        error: this.#bothSizesMessage(swap),
        sections: added.data.sections,
        cart: null,
        newKey: addedKey,
        messageVariantId: swap.fromVariantId,
        changed: true,
      };
    }

    // Refused, or unreadable: the take-off may still have happened, so check the cart.
    if (!removed.ok) return this.#checkRefusal(swap, SIZE_SWAP_UNKNOWN, addedKey, 'two-step refused');

    return this.#checkSwap(swap, removed.data, addedKey, 'two-step');
  }

  /**
   * #229: the old line as the cart has it NOW (/cart.js): by its old key if that still exists,
   * else the old size's only line, else its first plain line holding at least the moved
   * quantity. Null when it cannot be told, or the read fails.
   * @param {SizeSwap} swap
   * @returns {Promise<{key: string, quantity: number} | null>}
   */
  async #findOldLineNow(swap) {
    const cart = await this.#readCartNow();
    if (!cart) return null;

    /** @type {Array<{key: string, variant_id: number, quantity: number, properties?: Record<string, unknown> | null}>} */
    const lines = cart.items.filter(
      (/** @type {{variant_id: number}} */ item) => String(item.variant_id) === swap.fromVariantId
    );

    return (
      lines.find((item) => item.key === swap.lineKey) ??
      (lines.length === 1 ? lines[0] : undefined) ??
      lines.find((item) => isPlainLine(item) && item.quantity >= swap.quantity) ??
      null
    );
  }

  /**
   * @param {SizeSwap} swap
   * @returns {string} Both sizes are in the bag. Never "try again": that would add more.
   */
  #bothSizesMessage(swap) {
    return `Size ${swap.to} was added, but your ${swap.from} is still in your bag too. Please remove the one you don't want.`;
  }

  /**
   * #229: judges the cart Shopify returned by UNIT COUNTS per variant, not by line keys, which
   * Shopify changes whenever a line's discounts change (this store's bundle discounts count
   * across lines). `removed` is how many units left the old size, `arrived` how many reached
   * the new one; a clean move has both at least the line's quantity.
   *  - moved:     both are.
   *  - unchanged: neither moved; nothing happened, so "try again" is safe.
   *  - both:      some arrived but the old size was not (all) taken off: both sizes are in the
   *               bag, and the customer is told so, never asked to retry.
   *  - lost:      units left the old size and none arrived.
   *  - short:     units left the old size and fewer arrived.
   * lost and short are put right by #restoreMissing. Keys are used only to find a line to
   * focus or to show a message on.
   * @param {SizeSwap} swap
   * @param {any} cart - The cart JSON returned.
   * @param {string | null} preferredKey - The new line's key if the request reported it.
   * @param {string} mode - 'one-request' or 'two-step', for the console warning.
   * @returns {SizeSwapResult}
   */
  #checkSwap(swap, cart, preferredKey, mode) {
    /** @type {Array<{key: string, variant_id: number, quantity: number, properties?: Record<string, unknown> | null}>} */
    const items = cart.items;
    const unitsOf = (/** @type {string} */ variantId) =>
      items.reduce((sum, item) => (String(item.variant_id) === variantId ? sum + item.quantity : sum), 0);

    const removed = swap.fromInCart - unitsOf(swap.fromVariantId);
    const arrived = unitsOf(swap.toVariantId) - swap.inCart;

    const ofNewSize = (/** @type {{variant_id: number}} */ item) => String(item.variant_id) === swap.toVariantId;
    const newItem =
      (preferredKey ? items.find((item) => item.key === preferredKey) : undefined) ??
      (swap.mergeKey ? items.find((item) => item.key === swap.mergeKey) : undefined) ??
      items.find((item) => ofNewSize(item) && isPlainLine(item)) ??
      items.find(ofNewSize);
    const oldItem =
      items.find((item) => item.key === swap.lineKey) ??
      items.find((item) => String(item.variant_id) === swap.fromVariantId && isPlainLine(item));

    /** @type {SizeSwapResult} */
    const base = { sections: cart.sections, cart, newKey: newItem?.key ?? null, arrived, missing: 0 };

    // More moved than this swap asked for: the cart changed some other way too (another tab, or
    // a baseline that is out of date). Nothing is put back on a guess; the customer is asked to
    // look, and the cart is shown as the server has it.
    if (removed > swap.quantity || arrived > swap.quantity) {
      console.warn('[cart-size-change] the cart changed more than this swap asked', { removed, arrived, quantity: swap.quantity, mode });
      return { ...base, outcome: 'unknown', error: SIZE_SWAP_UNKNOWN, changed: true };
    }

    if (removed >= swap.quantity && arrived >= swap.quantity) return { ...base, outcome: 'moved' };

    console.warn('[cart-size-change] the cart did not change as asked', {
      removed,
      arrived,
      quantity: swap.quantity,
      mode,
    });

    if (removed <= 0 && arrived <= 0) {
      return {
        ...base,
        outcome: 'unchanged',
        error: SIZE_SWAP_FAILED,
        messageKey: oldItem?.key ?? null,
        messageVariantId: swap.fromVariantId,
      };
    }

    if (arrived > 0 && removed < swap.quantity) {
      return {
        ...base,
        outcome: 'both',
        error: this.#bothSizesMessage(swap),
        messageKey: oldItem?.key ?? null,
        messageVariantId: swap.fromVariantId,
        changed: true,
      };
    }

    return {
      ...base,
      outcome: arrived > 0 ? 'short' : 'lost',
      missing: removed - Math.max(arrived, 0),
      changed: true,
    };
  }

  /**
   * #229: puts back the units that left the old size without reaching the new one (#checkSwap's
   * lost and short), with /cart/add.js, and words the outcome. "Only N available" is said only
   * of a NEW line, where Shopify capping at stock is the likely cause; a top-up of a line
   * already in the cart is never capped by /cart/update (Shopify's Ajax reference: it "doesn't
   * validate the quantity on variants that are already in the cart"), so a short merge is
   * described without blaming stock.
   * @param {SizeSwap} swap
   * @param {SizeSwapResult} result
   * @returns {Promise<SizeSwapResult>}
   */
  async #restoreMissing(swap, result) {
    const missing = result.missing ?? 0;
    const arrived = result.arrived ?? 0;
    const newLine = swap.inCart === 0;
    const stays = missing === 1 ? 'stays' : 'stay';

    /** @type {{ok: boolean, data: any, message: string} | null} */
    let restored = null;
    try {
      restored = await this.#postCart(Theme.routes.cart_add_url, {
        items: [{ id: Number(swap.fromVariantId), quantity: missing }],
        sections: this.#sectionsToUpdate().join(','),
        sections_url: window.location.pathname,
      });
    } catch (error) {
      // The put-back may or may not have reached Shopify: never invite a re-add that could
      // double the units; show the cart as the server has it and ask for a look.
      console.error(error);
      return { ...result, error: SIZE_SWAP_UNKNOWN, rerender: true, changed: true };
    }

    if (restored?.ok) {
      let error = `Size ${swap.to} could not be added, so your ${swap.from} is back in your bag.`;
      if (result.outcome === 'short') {
        error = newLine
          ? `Only ${arrived} available in size ${swap.to}, so ${missing} ${stays} in size ${swap.from}.`
          : `Only ${arrived} could move to size ${swap.to}, so ${missing} ${stays} in size ${swap.from}.`;
      }

      return {
        ...result,
        error,
        sections: restored.data.sections,
        cart: null,
        messageKey: restored.data.items?.[0]?.key ?? null,
        messageVariantId: swap.fromVariantId,
        changed: true,
      };
    }

    // Only a 4xx carrying Shopify's own message is a refusal, which leaves nothing put back. A
    // 5xx or an unreadable answer may have committed, and /cart/add.js ADDS, so "add it again"
    // could double the units: treat it like a dropped request.
    const refused =
      restored !== null &&
      restored.status >= 400 &&
      restored.status < 500 &&
      Boolean(restored.data?.description || restored.data?.message);
    if (!refused) return { ...result, error: SIZE_SWAP_UNKNOWN, rerender: true, changed: true };

    const error =
      result.outcome === 'short'
        ? `${missing} of your ${swap.from} could not be moved to size ${swap.to} or put back. Please add ${missing === 1 ? 'it' : 'them'} again.`
        : `Size ${swap.to} could not be added, and your ${swap.from} was removed. Please add it again.`;

    // With no sections in hand (a refusal checked by /cart.js), re-draw from the server so the
    // header gets the new count.
    return {
      ...result,
      error,
      messageKey: result.newKey ?? null,
      messageVariantId: swap.toVariantId,
      changed: true,
      rerender: !result.sections,
    };
  }

  /**
   * #229: POSTs JSON to a cart endpoint. A failure comes back as `ok: false` with the message
   * Shopify gave, if any; only a network failure throws.
   * @param {string} url
   * @param {object} payload
   * @returns {Promise<{ok: boolean, status: number, data: any, message: string}>}
   */
  async #postCart(url, payload) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));

    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    const ok = response.ok && data !== null && Array.isArray(data.items);
    const shopifyMessage =
      data && (data.description || data.message || (typeof data.errors === 'string' ? data.errors : ''));

    return { ok, status: response.status, data, message: ok ? '' : shopifyMessage || SIZE_SWAP_FAILED };
  }

  /**
   * #229: shows the cart a swap left behind and tells the rest of the page.
   * @param {SizeSwapResult} result
   */
  async #applySwapResult(result) {
    /** @type {Record<string, string>} */
    const sections = result.sections ?? {};
    const itemCount = this.#readItemCount(sections[this.sectionId]);

    if (result.cart) this.#updateQuantitySelectors(result.cart);

    /** @type {{source: string, sections: Record<string, string>, itemCount?: number}} */
    const data = { source: 'cart-size-change', sections };
    if (typeof itemCount === 'number') data.itemCount = itemCount;

    // An unchanged cart is not an update: in the drawer it would be logged as a quantity change.
    if (result.outcome !== 'unchanged') this.dispatchEvent(new CartUpdateEvent({}, this.sectionId, data));

    if (sections[this.sectionId]) {
      await morphSection(this.sectionId, sections[this.sectionId]);
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * #229: the header count, read the way snippets/cart-display-count.liquid asks every producer
   * to (#150): the span gives the count; no span but the empty state means 0; neither means the
   * response was not what we expected, so no count at all rather than a guess.
   * @param {string | undefined} html - This component's section HTML.
   * @returns {number | undefined}
   */
  #readItemCount(html) {
    if (!html) return undefined;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const countElement = doc.querySelector('[ref="cartItemCount"]');

    if (countElement) {
      const parsed = parseInt(String(countElement.textContent).trim(), 10);
      return Number.isNaN(parsed) ? undefined : Math.max(0, parsed);
    }

    return doc.querySelector('.cart-items__empty-state') ? 0 : undefined;
  }

  /**
   * #229: shows a message in a line's own error slot (the `cartItemError-N` ref in
   * snippets/cart-products.liquid). Tries each key in turn, then a row of each variant, since a
   * failed change can leave either line on the page and Shopify may have re-keyed it. Found
   * through the row rather than by line number, which a re-render can change. With no row to
   * show it on, the status region shows it, visibly.
   * @param {Array<string | null | undefined>} keys
   * @param {string} message
   * @param {Array<string | null | undefined>} [variantIds]
   * @returns {HTMLElement | null} The row it was shown on, if any.
   */
  #showSwapError(keys, message, variantIds = []) {
    const rows = [
      ...keys.map((key) => (key ? this.#rowForKey(key) : undefined)),
      ...variantIds.map((variantId) => this.#rowForVariant(variantId)),
    ];

    for (const row of rows) {
      const text = row?.querySelector('[ref^="cartItemError-"]');
      const container = row?.querySelector('[ref^="cartItemErrorContainer-"]');

      if (row && text instanceof HTMLElement && container instanceof HTMLElement) {
        text.textContent = message;
        container.classList.remove('hidden');
        return row;
      }
    }

    this.#announce(message, true);
    return null;
  }

  /**
   * #229: after a request that failed or never answered, fetches this section again so the
   * page shows the cart as the server has it. Otherwise rows could keep keys and line numbers
   * the server no longer has, and the next - or + (which addresses a line by its NUMBER) could
   * change a different product. Tells the header only if the count actually moved, so a
   * refused request does not show up in analytics as a quantity change. Never throws.
   */
  async #rerenderAfterFailure() {
    try {
      const before = parseInt(String(this.querySelector('[ref="cartItemCount"]')?.textContent ?? '').trim(), 10);
      const html = await sectionRenderer.renderSection(this.sectionId, { cache: false });
      const after = this.#readItemCount(html);

      if (typeof after === 'number' && after !== before) {
        this.dispatchEvent(new CartUpdateEvent({}, this.sectionId, { source: 'cart-size-change', itemCount: after }));
      }

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * #229: after a failed change has re-drawn the cart, keyboard focus may have been on a size
   * button that is now hidden or gone, which drops it to the page. Puts it back on the line
   * the message is on, or on the first control left in the cart.
   * @param {HTMLElement | null} row
   */
  #recoverFocus(row) {
    const active = document.activeElement;
    const lost =
      !active || active === document.body || (this.contains(active) && active.closest('[hidden]') !== null);
    if (!lost) return;

    // With no line to stand on, the visible notice itself (made focusable by #announce): that
    // scrolls it into view in the drawer's list and puts a screen reader on the message.
    const notice = row ? null : this.querySelector('[data-cart-size-status].cart-items__size-notice');
    const target =
      row?.querySelector('.cart-items__size-toggle') ??
      row?.querySelector('a[href], button, input') ??
      notice ??
      this.querySelector('.cart-items__size-toggle, a[href], button');
    if (target instanceof HTMLElement) target.focus();
  }

  /**
   * #229: hides and empties a visible size notice. Runs when the next size change starts and
   * when the drawer closes. The notice survives re-renders in between (data-skip-node-update),
   * because the cart's own scripts re-render it moments after a failed change, Shipping
   * Protection's orphan removal among them, which would otherwise hide it before it was read.
   */
  #clearSizeNotice = () => {
    const region = this.querySelector('[data-cart-size-status]');
    if (!(region instanceof HTMLElement) || !region.classList.contains('cart-items__size-notice')) return;

    region.classList.add('visually-hidden');
    region.classList.remove('cart-items__size-notice');
    region.removeAttribute('tabindex');
    region.textContent = '';
  };

  /**
   * #229: speaks a message through the polite status region in snippets/cart-products.liquid.
   * Emptied first and refilled a moment later, so the same words twice are still read twice.
   * `visible` also shows it on screen, for a message with no cart line to sit on. It stays
   * through re-renders (data-skip-node-update) until #clearSizeNotice.
   * @param {string} message
   * @param {boolean} [visible]
   */
  #announce(message, visible = false) {
    const region = this.querySelector('[data-cart-size-status]');
    if (!(region instanceof HTMLElement)) return;

    region.classList.toggle('visually-hidden', !visible);
    region.classList.toggle('cart-items__size-notice', visible);

    if (visible) {
      // Shown at once, and focusable so #recoverFocus can bring it into view.
      region.setAttribute('tabindex', '-1');
      region.textContent = message;
      return;
    }

    region.removeAttribute('tabindex');
    region.textContent = '';
    setTimeout(() => {
      region.textContent = message;
    }, 100);
  }

  /**
   * #229: the analytics event. window.lsdTrack is defined in layout/theme.liquid and does
   * nothing until PostHog has loaded, which waits on consent; guarded so a missing or
   * throwing tracker can never break the cart.
   * @param {SizeSwap} swap
   */
  #trackSizeChange(swap) {
    const track = /** @type {any} */ (window).lsdTrack;
    if (typeof track !== 'function') return;

    try {
      track('cart_size_changed', {
        product_id: Number(swap.productId),
        from_variant_id: Number(swap.fromVariantId),
        to_variant_id: Number(swap.toVariantId),
        from: swap.from,
        to: swap.to,
        placement: this.closest('cart-drawer-component') ? 'drawer' : 'cart_page',
      });
    } catch (error) {
      // Analytics must never break the cart.
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = [].concat(this.refs.quantitySelectors ?? []).find(
      (sel) => sel.dataset.lineIndex === String(line)
    );
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
